"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import { rmcClient } from "@/lib/rmcClient";
import { useAppStore } from "@/lib/store";
import { useQuery } from "@tanstack/react-query";
import React from "react";

export default function RmcLedger() {
  const fy = useAppStore((s) => s.activeFY);
  const [ledgerType, setLedgerType] = React.useState("sales");
  const [partyId, setPartyId] = React.useState(null);
  const [from, setFrom] = React.useState(fy?.start_date || "2025-04-01");
  const [to, setTo] = React.useState(new Date().toISOString().slice(0, 10));

  React.useEffect(() => {
    if (fy?.start_date) setFrom(fy.start_date);
  }, [fy]);

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: purchaseParties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });
  const { data: company } = useQuery({
    queryKey: ["company"],
    queryFn: async () => (await rmcClient.getCompany()).data,
  });
  const { data: rows = [], refetch } = useQuery({
    queryKey: ["ledger", ledgerType, partyId, from, to],
    queryFn: async () =>
      partyId
        ? ledgerType === "sales"
          ? (
              await rmcClient.getLedger(partyId, {
                date_from: from || undefined,
                date_to: to || undefined,
              })
            ).data
          : (
              await rmcClient.getPurchaseLedger(partyId, {
                date_from: from || undefined,
                date_to: to || undefined,
              })
            ).data
        : [],
    enabled: !!partyId,
  });

  const { data: bal } = useQuery({
    queryKey: ["partyBal", ledgerType, partyId],
    queryFn: async () =>
      partyId
        ? ledgerType === "sales"
          ? (await rmcClient.getPartyBalance(partyId)).data
          : (await rmcClient.getPurchasePartyBalance(partyId)).data
        : null,
    enabled: !!partyId,
  });

  const buildParticular = React.useCallback(
    (entry) => {
      const grade = entry?.grade ?? "";
      const base = entry?.particular || (grade ? `Grade ${grade}` : "—");
      if (ledgerType !== "purchase") return base;

      const challanNo =
        entry?.challan_no ||
        entry?.purchase_challan_no ||
        entry?.purchase_challan_number ||
        entry?.purchase_challan?.challan_no ||
        entry?.challan?.challan_no ||
        "";
      if (challanNo) return String(challanNo);

      const referenceNo = String(entry?.reference_no || "").trim();
      if (/^PUR/i.test(referenceNo)) return referenceNo;

      // Fallback: if API only sends descriptive text, extract PUR challan token.
      const fromText = String(base).match(/\bPUR[A-Z0-9-]*\d+\b/i)?.[0];
      if (fromText) return fromText;

      const remark = String(entry?.remark || "").trim();
      if (remark) return remark;

      return base;
    },
    [ledgerType]
  );

  const withBal = React.useMemo(() => {
    const normalized = rows.map((e, idx) => {
      const debit = Number(e.debit || 0);
      const credit = Number(e.credit || 0);
      const run = Number(e.running_balance);
      const balanceType = e.balance_type || (run >= 0 ? "Dr" : "Cr");
      const date = e.entry_date ?? e.date ?? "—";
      const grade = e.grade ?? "";
      return {
        ...e,
        __idx: idx,
        date,
        grade,
        particular: buildParticular(e),
        debit: Number.isFinite(debit) ? debit : 0,
        credit: Number.isFinite(credit) ? credit : 0,
        run: Number.isFinite(run) ? run : debit - credit,
        balanceType,
      };
    });

    // If date + grade repeat, merge into a single total row.
    const grouped = [];
    const keyToIndex = new Map();
    normalized.forEach((row) => {
      const canGroup = row.grade && row.date && row.date !== "—";
      const key = canGroup ? `${row.date}__${row.grade}` : `row__${row.__idx}`;
      const hit = keyToIndex.get(key);
      if (hit == null) {
        keyToIndex.set(key, grouped.length);
        grouped.push({
          ...row,
          particular:
            ledgerType === "purchase"
              ? row.particular
              : row.grade
                ? `Grade ${row.grade}`
                : row.particular,
        });
        return;
      }
      const prev = grouped[hit];
      grouped[hit] = {
        ...prev,
        debit: prev.debit + row.debit,
        credit: prev.credit + row.credit,
        // Keep latest running balance for that grouped point.
        run: row.run,
        balanceType: row.balanceType,
      };
    });

    const seenDates = new Set();
    return grouped.map((row) => {
      const showDate = row.date && row.date !== "—" && !seenDates.has(row.date);
      if (row.date && row.date !== "—") seenDates.add(row.date);
      return {
        ...row,
        dateDisplay: showDate ? row.date : "",
      };
    });
  }, [buildParticular, ledgerType, rows]);

  const outstandingValue = React.useMemo(() => {
    if (bal == null) return Number.NaN;
    if (typeof bal === "number" || typeof bal === "string") return Number(bal);
    const candidate =
      bal.balance ??
      bal.outstanding ??
      bal.outstanding_balance ??
      bal.closing_balance ??
      bal.amount ??
      bal.net_balance;
    return Number(candidate);
  }, [bal]);
  const hasOutstanding = Number.isFinite(outstandingValue);
  const outstandingLabel =
    bal?.label || bal?.balance_type || bal?.type || (outstandingValue >= 0 ? "Dr" : "Cr");

  const totals = React.useMemo(
    () =>
      withBal.reduce(
        (acc, row) => ({
          debit: acc.debit + Number(row.debit || 0),
          credit: acc.credit + Number(row.credit || 0),
        }),
        { debit: 0, credit: 0 }
      ),
    [withBal]
  );

  const money = (val) =>
    Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const printLedger = () => {
    const party =
      ledgerType === "sales"
        ? parties.find((p) => p.id === partyId)
        : purchaseParties.find((p) => p.id === partyId);
    const formatDate = (value) => {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Ledger</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        body { font-family: Arial, sans-serif; color:#111; margin: 0; background: #fff; }
        .sheet { width: 200mm; margin: 0 auto; }
        .head { margin-bottom: 12px; border: 1px solid #333; padding: 10px; }
        .company { font-size: 20px; font-weight: 700; margin: 0; }
        .company-sub { margin-top: 3px; font-size: 12px; color:#333; }
        .company-meta-row { margin-top: 2px; display: flex; justify-content: space-between; align-items: center; font-size: 11px; color:#333; }
        .company-center { text-align: center; }
        .ledger-title { margin-top: 8px; font-size: 14px; font-weight: 700; border-top: 1px solid #333; padding-top: 6px; }
        .meta { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .meta td { border: 1px solid #333; padding: 5px 7px; font-size: 12px; }
        .meta .label { width: 18%; font-weight: 700; background: #f3f3f3; }
        .meta .value { font-weight: 600; }
        table { width:100%; border-collapse:collapse; margin-top:10px; border: 1px solid #333; }
        thead { display: table-header-group; }
        tfoot { display: table-row-group; }
        tr, td, th { page-break-inside: avoid; break-inside: avoid; }
        th, td { border:1px solid #333; padding:6px 8px; font-size:12px; }
        th { background:#f3f3f3; }
        td.num, th.num { text-align:right; }
        .right { text-align:right; }
        .tfoot td { font-weight:700; background:#fafafa; }
        .bal-dr { color: #b91c1c; }
        .bal-cr { color: #15803d; }
      </style>
      </head><body>`);
    w.document.write(`<div class="sheet"><div class="head">
      ${company?.blessing ? `<div style="font-style: italic; margin-bottom: 6px; font-size: 12px; color: #333; text-align: center;">${company.blessing}</div>` : ""}
      <div class="company-center">
        <p class="company">${company?.name || ""}</p>
        <div class="company-sub">${company?.address || ""}</div>
      </div>
      <div class="company-meta-row">
        <span>GST: ${company?.gst_number || "—"}</span>
        <span>Mobile: ${company?.contact_phone || "—"}</span>
      </div>
      <div class="ledger-title">Ledger Statement</div>
      <table class="meta">
        <tr>
          <td class="label">Party Name</td>
          <td class="value">${party?.name || "—"}</td>
          <td class="label">Ledger Type</td>
          <td class="value">${ledgerType === "sales" ? "Sales" : "Purchase"}</td>
        </tr>
        <tr>
          <td class="label">Party Address</td>
          <td class="value" colspan="3">${party?.address || "—"}</td>
        </tr>
        <tr>
          <td class="label">Period</td>
          <td class="value">${formatDate(from)} to ${formatDate(to)}</td>
          <td class="label">Printed On</td>
          <td class="value">${formatDate(new Date().toISOString())}</td>
        </tr>
      </table>
    </div>`);
    w.document.write(`<table><thead><tr><th>Date</th><th>Particular</th><th class="num">Dr</th><th class="num">Cr</th><th class="num">Balance</th></tr></thead><tbody>`);
    withBal.forEach((e) => {
      const drCr = e.balanceType || (e.run >= 0 ? "Dr" : "Cr");
      w.document.write(
        `<tr><td>${e.dateDisplay || ""}</td><td>${e.particular}</td><td class="num">${money(e.debit)}</td><td class="num">${money(e.credit)}</td><td class="num">${money(Math.abs(e.run))} ${drCr}</td></tr>`
      );
    });
    w.document.write(`</tbody>`);
    w.document.write(
      `<tr class="tfoot"><td colspan="2" class="right">Sub Total</td><td class="num">${money(totals.debit)}</td><td class="num">${money(totals.credit)}</td><td class="num"></td></tr>`
    );
    if (hasOutstanding) {
      const outClass = String(outstandingLabel).toLowerCase() === "cr" ? "bal-cr" : "bal-dr";
      w.document.write(
        `<tr class="tfoot"><td colspan="4" class="right">Outstanding</td><td class="num ${outClass}">${money(
          Math.abs(outstandingValue)
        )} ${outstandingLabel}</td></tr>`
      );
    }
    w.document.write(`</table></div></body></html>`);
    w.document.close();
    w.print();
  };
  const selectedPartyName =
    (ledgerType === "sales"
      ? parties.find((p) => p.id === partyId)
      : purchaseParties.find((p) => p.id === partyId)
    )?.name || "";

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Party Ledger" />
      <div className="rounded-xl border border-gray-800 bg-gray-950/40 p-4">
        <div className="grid gap-4 xl:grid-cols-12 xl:items-end">
          <div className="xl:col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Ledger Type</p>
            <div className="mt-2 inline-flex rounded-lg border border-gray-800 bg-gray-950 p-1">
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  ledgerType === "sales"
                    ? "bg-white text-black"
                    : "text-gray-300 hover:bg-white/5"
                }`}
                onClick={() => {
                  setLedgerType("sales");
                  setPartyId(null);
                }}
              >
                Sales
              </button>
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  ledgerType === "purchase"
                    ? "bg-white text-black"
                    : "text-gray-300 hover:bg-white/5"
                }`}
                onClick={() => {
                  setLedgerType("purchase");
                  setPartyId(null);
                }}
              >
                Purchase
              </button>
            </div>
          </div>

          <div className="xl:col-span-4">
            <SearchableSelect
              label={ledgerType === "sales" ? "Sales Party" : "Purchase Party"}
              value={partyId}
              onChange={setPartyId}
              options={ledgerType === "sales" ? parties : purchaseParties}
              placeholder={`Select ${ledgerType === "sales" ? "sales" : "purchase"} party…`}
            />
          </div>

          <div className="xl:col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">From</p>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-2" />
          </div>
          <div className="xl:col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">To</p>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-2" />
          </div>
          <div className="xl:col-span-2 flex gap-2 xl:justify-end">
            <Button size="sm" variant="outline" className="h-11 px-4" onClick={printLedger}>
              Print
            </Button>
          </div>
        </div>
        {selectedPartyName && (
          <div className="mt-3 border-t border-gray-800 pt-3">
            <p className="text-xs text-gray-400">
              Viewing <span className="font-semibold text-white">{selectedPartyName}</span> ledger for{" "}
              <span className="font-semibold text-white">{from}</span> to{" "}
              <span className="font-semibold text-white">{to}</span>.
            </p>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
        <div className="border-b border-gray-800 pb-3 text-center">
          {company?.blessing && <p className="text-sm italic text-gray-300">{company.blessing}</p>}
          <h2 className="text-lg font-semibold text-white">{company?.name || "Company"}</h2>
          <p className="text-xs text-gray-400">{company?.address || ""}</p>
          <p className="mt-2 text-sm font-medium text-gray-400">Party Name:</p>
          <p className="text-xl font-bold text-gray-100">
            {selectedPartyName || "Party Ledger"}
          </p>
          <p className="mt-1 text-base font-semibold text-gray-300">
            {from} to {to}
          </p>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-y border-gray-800 text-left text-gray-400">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Particular</th>
                <th className="px-3 py-2 text-right font-medium">Debit (₹)</th>
                <th className="px-3 py-2 text-right font-medium">Credit (₹)</th>
                <th className="px-3 py-2 text-right font-medium">Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {withBal.map((r, i) => (
                <tr key={`${r.date}-${r.particular}-${i}`} className="border-b border-gray-900">
                  <td className="px-3 py-2 text-gray-300">{r.dateDisplay || " "}</td>
                  <td className="px-3 py-2 text-gray-200">{r.particular}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{money(r.debit)}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{money(r.credit)}</td>
                  <td
                    className={`px-3 py-2 text-right font-medium ${
                      r.balanceType === "Dr" ? "text-red-400" : "text-green-400"
                    }`}
                  >
                    ₹{money(Math.abs(r.run))} {r.balanceType}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-gray-800 bg-gray-900/30 font-semibold">
                <td colSpan={2} className="px-3 py-2 text-right text-gray-300">
                  Sub Total
                </td>
                <td className="px-3 py-2 text-right text-gray-100">{money(totals.debit)}</td>
                <td className="px-3 py-2 text-right text-gray-100">{money(totals.credit)}</td>
                <td className="px-3 py-2" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {partyId && hasOutstanding && (
        <p className="text-sm text-white">
          Outstanding: ₹
          {money(Math.abs(outstandingValue))}
          {" "}
          <span className={outstandingValue >= 0 ? "text-red-400" : "text-green-400"}>
            {outstandingLabel}
          </span>
        </p>
      )}
    </div>
  );
}
